import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './App';
import './styles/commander.css';

/**
 * Gracefully dismiss the splash screen.
 *
 * Adds the `splash--hidden` class which triggers a smooth CSS
 * fade-out + scale transition (450ms), then removes the element
 * from the DOM once the transition completes. A minimum display
 * duration of 800ms ensures the splash feels intentional even on
 * very fast connections -- premium loading screens should never
 * flash and vanish.
 */
function dismissSplash(): void {
  const splash = document.getElementById('splash');
  if (!splash) return;

  const MINIMUM_DISPLAY_MS = 800;
  const started = performance.now();
  const elapsed = started - (window.__splashStart ?? started);
  const remaining = Math.max(0, MINIMUM_DISPLAY_MS - elapsed);

  setTimeout(() => {
    splash.classList.add('splash--hidden');

    const onDone = () => {
      splash.removeEventListener('transitionend', onDone);
      splash.remove();
    };
    splash.addEventListener('transitionend', onDone);

    // Safety net: remove after 600ms even if transitionend fails to fire
    setTimeout(() => {
      if (splash.parentNode) splash.remove();
    }, 600);
  }, remaining);
}

// Record the moment this script executes so we can calculate
// how long the splash has already been visible.
declare global {
  interface Window {
    __splashStart?: number;
  }
}
window.__splashStart = performance.now();

async function bootstrap() {
  // Enable MSW mocking when VITE_MOCK_API is set (dev or demo builds)
  if (import.meta.env.VITE_MOCK_API === 'true') {
    const { startMocking } = await import('@commander/api-client/mocks');
    await startMocking();
    console.log('[MSW] Mocking enabled');
  }

  const rootElement = document.getElementById('root');
  if (!rootElement) {
    throw new Error('Root element not found');
  }

  createRoot(rootElement).render(
    <StrictMode>
      <App />
    </StrictMode>
  );

  // Dismiss splash once React has rendered into the DOM
  dismissSplash();
}

bootstrap();
